import React, { useId } from 'react';
import { Dialog, DialogContent, Box, Button, Flex, Heading, Text } from '@indeed/ifl-components';
import { Close } from '@indeed/ifl-icons';
import GettextFactory from '../../../util/GettextFactory';
import { ApplyForMePromoSvg } from '../../../components/Illustrations';

export interface ApplyForMePromoModalProps {
    /** Controls whether the modal is rendered. */
    isOpen: boolean;
    /** Fired when the user chooses to set up Apply For Me (primary action). */
    onSetUp: () => void;
    /** Fired when the user chooses to skip setup for now (secondary action). */
    onContinue: () => void;
    /** Fired when the modal is dismissed (close button, backdrop, or Escape key). */
    onClose: () => void;
    /**
     * Render the dialog full screen. Used when the modal is presented inside a
     * native JSMABridge webview (see `ApplyForMePromoPage`); defaults to the
     * bottom-sheet presentation on web.
     */
    fullScreen?: boolean;
    /** Hide the web close button when native bottom-sheet chrome handles dismissal. */
    inNativeBottomSheet?: boolean;
}

// Subtle pink→blue header wash from the promo design (10% opacity over the white
// sheet). Expects a transparent illustration so the gradient shows behind it.
const HEADER_GRADIENT =
    'linear-gradient(160deg, rgba(92, 141, 255, 0.1) 0%, rgba(255, 171, 216, 0.1) 100%)';

/**
 * The Apply For Me upsell promo, surfaced after the user taps "Back to homepage"
 * on the auto-apply success page. Presented as a bottom sheet on web and full
 * screen inside a native JSMABridge webview (see `ApplyForMePromoPage`).
 *
 * Presentational only: the parent owns navigation and analytics via the
 * `onSetUp` / `onContinue` / `onClose` callbacks.
 */
const ApplyForMePromoModal: React.FC<ApplyForMePromoModalProps> = ({
    isOpen,
    onSetUp,
    onContinue,
    onClose,
    fullScreen = false,
    inNativeBottomSheet = false,
}) => {
    const gt = GettextFactory();
    const headingId = useId();
    const descriptionId = useId();

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
            size="sm"
            type="dialog"
            // Resolve the bottom-sheet vs centered presentation from the viewport
            // breakpoint. On native we render full screen instead, which takes
            // precedence over `mobile`.
            mobile="auto"
            fullScreen={fullScreen}
        >
            {/* Drop the default content padding so the illustration header can run
                edge to edge; spacing is reapplied on the content section below. */}
            <DialogContent
                aria-labelledby={headingId}
                aria-describedby={descriptionId}
                sx={{ padding: 0 }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        // Anchor the artwork to the bottom so the blob bleeds into the
                        // content edge, leaving gradient space up top (matching design).
                        alignItems: 'flex-end',
                        inlineSize: '100%',
                        paddingBlockStart: !inNativeBottomSheet ? 7 : 0,
                        paddingBlockEnd: 0,
                        backgroundImage: !inNativeBottomSheet ? HEADER_GRADIENT : undefined,
                        borderStartStartRadius: 'lg',
                        borderStartEndRadius: 'lg',
                    }}
                >
                    {!inNativeBottomSheet && (
                        <Button
                            variant="tertiary"
                            size="md"
                            iconOnly
                            onClick={onClose}
                            aria-label={gt.gettext('Close')}
                            sx={{ position: 'absolute', insetBlockStart: 2, insetInlineEnd: 2 }}
                        >
                            <Close aria-hidden />
                        </Button>
                    )}

                    <ApplyForMePromoSvg
                        aria-hidden
                        style={{ display: 'block', height: '7rem', width: 'auto' }}
                    />
                </Box>

                <Flex
                    sx={{
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        paddingInline: 3,
                        paddingBlockStart: 3,
                        paddingBlockEnd: 4,
                        gap: 5,
                    }}
                >
                    <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Heading
                            id={headingId}
                            level={4}
                            sx={{ fontWeight: 'medium', marginBlock: 0 }}
                        >
                            {gt.gettext('Automate your applications')}
                        </Heading>

                        <Text
                            id={descriptionId}
                            level={2}
                            variant="secondary"
                            sx={{ textAlign: 'start' }}
                        >
                            {gt.gettext(
                                'Apply For Me finds and applies to jobs for you. Set your preferences and pause anytime.',
                            )}
                        </Text>
                    </Flex>

                    <Flex
                        sx={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            inlineSize: '100%',
                            gap: 2,
                        }}
                    >
                        <Button variant="primary" size="lg" full onClick={onSetUp}>
                            {gt.gettext('Set up')}
                        </Button>
                        <Button variant="tertiary" size="lg" full onClick={onContinue}>
                            {gt.gettext('Skip for now')}
                        </Button>
                    </Flex>
                </Flex>
            </DialogContent>
        </Dialog>
    );
};

export default ApplyForMePromoModal;
